import type { Match } from "@spikeeasy/tournament";

export interface BracketNode {
  match: Match;
  column: number;
  row: number;
}
export interface BracketLayout {
  nodes: BracketNode[];
  headings: Array<{ label: string; column: number; row: number }>;
  edges: Array<{ from: string; to: string; outcome: "winner" | "loser" }>;
  columns: number;
}

export function bracketLayout(matches: Match[]): BracketLayout {
  const nodes: BracketNode[] = [];
  const headings: BracketLayout["headings"] = [];
  const byId = new Map(matches.map((match) => [match.id, match]));
  const isDouble = matches.some((match) => match.stage === "winner");
  const loserSemifinal = isDouble
    ? matches.filter((match) => match.stage === "semifinal").at(-1)?.id
    : undefined;
  const lane = (match: Match) => {
    if (!isDouble) return "main";
    if (match.stage === "semifinal")
      return match.id === loserSemifinal ? "loser" : "winner";
    return match.stage;
  };
  const stages = matches.filter(
    (match) =>
      match.stage !== "bronze" && (!isDouble || match.stage !== "final"),
  );
  const widths = new Map<string, number>();
  for (const match of stages) {
    const size = stages.filter(
      (other) => lane(other) === lane(match) && other.wave === match.wave,
    ).length;
    widths.set(lane(match), Math.max(widths.get(lane(match)) ?? 0, size));
  }
  const lowerOffset = (widths.get("winner") ?? 0) * 2 + 2;
  const lastColumn = new Map<string, number>();
  for (const wave of [...new Set(stages.map((match) => match.wave))].sort(
    (a, b) => a - b,
  )) {
    const round = stages.filter((match) => match.wave === wave);
    for (const side of [...new Set(round.map(lane))]) {
      const group = round.filter((match) => lane(match) === side);
      const parents = group.flatMap((match) =>
        match.slots.flatMap((slot) =>
          "matchId" in slot
            ? nodes.filter((node) => node.match.id === slot.matchId)
            : [],
        ),
      );
      const column = Math.max(
        (lastColumn.get(side) ?? -1) + 1,
        ...parents.map((node) => node.column + 1),
      );
      const offset = side === "loser" ? lowerOffset : 0;
      const first = group[0]!;
      const roundLabel =
        first.stage === "final"
          ? "Final"
          : first.stage === "semifinal"
            ? "Semifinal"
            : !isDouble && group.length === 4
              ? "Quarterfinals"
              : `Round ${first.round}`;
      headings.push({
        label: `${isDouble ? `${side === "loser" ? "Losers" : "Winners"} · ` : ""}${roundLabel}`,
        column,
        row: offset + 1,
      });
      group.forEach((match, index) => {
        const center = (index + 0.5) * (widths.get(side)! / group.length) - 0.5;
        nodes.push({ match, column, row: offset + 2 + center * 2 });
      });
      lastColumn.set(side, column);
    }
  }
  const final = matches.find((match) => match.stage === "final");
  if (isDouble && final) {
    const semis = nodes.filter((node) => node.match.stage === "semifinal");
    const column = Math.max(...nodes.map((node) => node.column)) + 1;
    const row = Math.round(
      semis.reduce((sum, node) => sum + node.row, 0) /
        Math.max(1, semis.length),
    );
    nodes.push({ match: final, column, row });
    headings.push({ label: "Final", column, row: row - 1 });
  }
  const bronze = matches.find((match) => match.stage === "bronze");
  const finalNode = nodes.find((node) => node.match.stage === "final");
  if (bronze && finalNode)
    nodes.push({
      match: bronze,
      column: finalNode.column,
      row: finalNode.row + 3,
    });
  const edges = nodes.flatMap((node) =>
    node.match.slots.flatMap((slot) =>
      "matchId" in slot && byId.has(slot.matchId)
        ? [{ from: slot.matchId, to: node.match.id, outcome: slot.outcome }]
        : [],
    ),
  );
  return {
    nodes,
    headings,
    edges,
    columns: Math.max(0, ...nodes.map((node) => node.column)) + 1,
  };
}
